/**
 * Vowella Gifts — shared engine for the customization snippets
 * (vowella-customize-necklace.liquid, vowella-customize-bracelet.liquid).
 *
 * The customize card renders OUTSIDE the actual <form> in the DOM (it sits
 * between Dawn's native Variant Picker block and the Buy Buttons block,
 * which is where the <form> itself lives). To guarantee its values are
 * actually submitted with Add to Cart, this script mirrors every field's
 * current name+value into a real hidden <input> that lives INSIDE the
 * form (rather than relying only on the HTML `form="..."` attribute,
 * which associates an element with a form by id but can be fragile across
 * browsers/theme setups) — mirrors are kept in sync on every change and
 * re-synced one more time in a capture-phase submit listener, right
 * before Dawn's own submit handler builds its FormData.
 *
 * Responsibilities:
 *  1. Mirror every customize field into the real Add to Cart <form> so its
 *     value is actually included in the add-to-cart request.
 *  2. Wire up the birthstone-style "gem" grids (click to select, one value
 *     per grid, mirrored into a hidden line-item-property input).
 *  3. Restore what the shopper typed/picked if they navigate back to this
 *     product (per-product, sessionStorage only — nothing is sent anywhere).
 *  4. Block Add to Cart with an inline message if a required gem grid has no
 *     selection yet (native `required` can't show a tooltip on a hidden
 *     input, so this is a small hand-rolled guard instead).
 *  5. Drive the "Preview Your Necklace" / "Preview Your Bracelet" panel(s) —
 *     once the required fields are filled in, composite the base photo
 *     (synced to the selected Color variant on every product that has one)
 *     with the typed name(s) and/or chosen gem overlay(s) — entirely
 *     client-side (layered <img>/<div> elements, no <canvas> — simpler,
 *     and nothing to taint/export). A card can hold MORE THAN ONE preview
 *     step, and a single step can itself stack more than one photo (see
 *     the couple's ID bracelet's "bar-pair" mode below, which renders Her
 *     bracelet stacked directly above His inside ONE step/frame, since
 *     they ship together as one set and the client wants them previewed
 *     as one combined image, not two separate preview panels) — every
 *     function below that touches a preview operates on ONE step element,
 *     and initPreview()/updatePreviewState() loop over every
 *     `[data-vowella-preview-step]` in the card. Each step's own
 *     data-vowella-preview-mode picks which fields are required and which
 *     overlays are drawn — see previewRefs() below:
 *       - "gem"         — necklace: 2 names + 2 stones (left/right).
 *       - "name"        — engraved bracelet: 2 names only, fixed bg.
 *       - "single-gem"  — heart-charm bracelet: 1 stone, no names at all,
 *                         bg synced to Color like "gem" mode is.
 *       - "single-name" — bar ID bracelet (single, one bracelet only):
 *                         1 engraving-text field, no birthstone, bg synced
 *                         to the Color variant.
 *       - "bar-pair"    — couple's ID bracelet SET: like "single-name" but
 *                         doubled and stacked in ONE frame — 2 engraving
 *                         fields, 2 independent background photos (Her on
 *                         top, His on bottom), one shared Preview button.
 *                         Sept 2026 (§35): Her and His each pick their own
 *                         Color independently (they don't have to match —
 *                         a shopper can order a gold Her + a silver His),
 *                         read via fieldValue(card, source, 'color') —
 *                         see topColorSource/bottomColorSource in
 *                         previewRefs() — same as every other
 *                         line-item-property field in this file, NOT a
 *                         real Shopify variant option (see §35 below).
 *  6. Drive the "Choose Your Engraving Font" grid (vowella-font-grid.liquid)
 *     — clicking a swatch sets the "Engraving Font" line-item property and
 *     updates --vowella-preview-font on the card so the preview panel's
 *     text re-renders in the chosen font immediately.
 *  7. Drive the "Gift For" (Recipient) grid on the bar ID bracelet
 *     (vowella-customize-id-bracelet.liquid) — clicking "Her"/"Him" sets a
 *     line-item property AND, if the merchant has tagged a product photo's
 *     Alt Text with the "Label_ForHim"/"Label_ForHer" convention, jumps
 *     Dawn's own product media gallery to that photo (see
 *     jumpGalleryToRecipient() below). Purely additive: a product with no
 *     tagged photos yet just does nothing here, never errors.
 *  8. Drive the "Her/His Bracelet Color" grids on the couple's ID bracelet
 *     (initColorGrid() below). Sept 2026 (§33) first tried this as a real
 *     Shopify variant option (so Dawn's own price/media/inventory logic
 *     would apply); §34 needed it doubled into two independent options
 *     (Her/His don't have to match); then the client flagged (§35) that 2
 *     colors × 2 colors as real variants means 9 SKUs to keep priced
 *     consistently — real risk of the price drifting between combinations
 *     for a choice that was never meant to change the price at all. §35
 *     drops the real-variant approach entirely: both color grids are now
 *     wired exactly like the birthstone "gem" grid on the necklace — click
 *     to select, one value stored in a hidden line-item-property input
 *     (properties[Her Bracelet Color] / properties[His Bracelet Color]),
 *     mirrored into the Add to Cart form like every other property here.
 *     No real variant, no price/inventory implications, one flat price
 *     regardless of the combination chosen — and the live preview reads
 *     the picked color the same way it reads engraving text, via
 *     fieldValue(). The product itself no longer needs ANY real option for
 *     this — see the updated IMPORTANT section in vowella-customize-id-
 *     bracelet.liquid.
 *
 * Runs for every `[data-vowella-cx]` card on the page (usually one).
 */
(function () {
  function getForm() {
    return (
      document.querySelector('product-form form[data-type="add-to-cart-form"]') ||
      document.querySelector('form[data-type="add-to-cart-form"]')
    );
  }

  function store(key) {
    try {
      return JSON.parse(sessionStorage.getItem(key) || '{}');
    } catch (e) {
      return {};
    }
  }

  function save(key, patch) {
    try {
      var s = store(key);
      for (var k in patch) s[k] = patch[k];
      sessionStorage.setItem(key, JSON.stringify(s));
    } catch (e) {
      /* sessionStorage unavailable — customization still works, just isn't restored */
    }
  }

  function mirrorContainer(form) {
    var mirror = form.querySelector('[data-vowella-cx-mirror]');
    if (!mirror) {
      mirror = document.createElement('div');
      mirror.setAttribute('data-vowella-cx-mirror', '');
      mirror.hidden = true;
      form.appendChild(mirror);
    }
    return mirror;
  }

  function mirrorField(form, field) {
    if (!field || !field.name) return;
    var key = field.getAttribute('data-vowella-cx-input') || field.name;
    var mirror = mirrorContainer(form);
    var mirrorInput = mirror.querySelector('[data-mirror-for="' + CSS.escape(key) + '"]');
    if (!mirrorInput) {
      mirrorInput = document.createElement('input');
      mirrorInput.type = 'hidden';
      mirrorInput.setAttribute('data-mirror-for', key);
      mirror.appendChild(mirrorInput);
    }
    mirrorInput.name = field.name;
    mirrorInput.value = field.value;
  }

  function mirrorAll(card, form) {
    if (!form) return;
    var fields = card.querySelectorAll('[data-vowella-cx-input]');
    for (var i = 0; i < fields.length; i++) mirrorField(form, fields[i]);
  }

  function wire(card) {
    var form = getForm();
    if (!form) return;

    mirrorAll(card, form);

    if (form.dataset.vowellaCxBound === '1') return;
    form.dataset.vowellaCxBound = '1';

    form.addEventListener(
      'submit',
      function (e) {
        var liveCard = document.querySelector('[data-vowella-cx]');
        if (!liveCard) return;

        // Final safety re-sync right before Dawn's own submit handler
        // reads FormData(form).
        mirrorAll(liveCard, form);

        var missing = null;
        var grids = liveCard.querySelectorAll('[data-vowella-gem-grid][data-required="true"]');
        for (var g = 0; g < grids.length; g++) {
          var hidden = liveCard.querySelector(
            '[data-vowella-gem-value="' + grids[g].getAttribute('data-vowella-gem-grid') + '"]'
          );
          if (hidden && !hidden.value) {
            missing = grids[g];
            break;
          }
        }

        if (missing) {
          e.preventDefault();
          e.stopImmediatePropagation();
          var err = missing.parentNode.querySelector('.vowella-cx-error');
          if (!err) {
            err = document.createElement('div');
            err.className = 'vowella-cx-error';
            missing.insertAdjacentElement('afterend', err);
          }
          err.textContent = 'Please choose a birthstone to continue.';
          missing.classList.add('is-missing');
          missing.scrollIntoView({ block: 'center', behavior: 'smooth' });
          var clear = function () {
            err.remove();
            missing.classList.remove('is-missing');
            missing.removeEventListener('click', clear);
          };
          missing.addEventListener('click', clear);
        }
      },
      true
    );
  }

  function initTextField(card, input, storageKey) {
    try {
      var name = input.getAttribute('data-vowella-cx-input');
      var saved = store(storageKey);
      if (saved[name] != null) input.value = saved[name];

      var counter = input.parentNode.querySelector('[data-vowella-cx-count]');
      var updateCount = function () {
        if (counter) counter.textContent = input.value.length;
      };
      updateCount();

      input.addEventListener('input', function () {
        updateCount();
        var patch = {};
        patch[name] = input.value;
        save(storageKey, patch);
        var form = getForm();
        if (form) mirrorField(form, input);
        updatePreviewState(card);
      });
    } catch (err) {
      /* one broken field should never take down the rest of the card */
      if (window.console) console.error('Vowella customize: text field init failed', err);
    }
  }

  function initGemGrid(card, grid, storageKey) {
    try {
      var key = grid.getAttribute('data-vowella-gem-grid');
      var hidden = card.querySelector('[data-vowella-gem-value="' + key + '"]');
      var echo = card.querySelector('[data-vowella-gem-echo="' + key + '"]');
      var saved = store(storageKey);

      function setEcho(val) {
        if (!echo) return;
        echo.textContent = val ? '— ' + val : '';
      }

      function select(gem, val, persist) {
        var all = grid.querySelectorAll('.vowella-gem');
        for (var i = 0; i < all.length; i++) all[i].classList.toggle('is-selected', all[i] === gem);
        if (hidden) hidden.value = val;
        setEcho(val);
        var form = getForm();
        if (form && hidden) mirrorField(form, hidden);
        if (persist) {
          var patch = {};
          patch[key] = val;
          save(storageKey, patch);
        }
        updatePreviewState(card);
      }

      if (saved[key]) {
        var match = grid.querySelector('.vowella-gem[data-val="' + CSS.escape(saved[key]) + '"]');
        if (match) select(match, saved[key], false);
      }

      grid.addEventListener('click', function (e) {
        var gem = e.target.closest('.vowella-gem');
        if (!gem || !grid.contains(gem)) return;
        select(gem, gem.getAttribute('data-val'), true);
      });
    } catch (err) {
      if (window.console) console.error('Vowella customize: gem grid init failed', err);
    }
  }

  function initFontGrid(card, grid, storageKey) {
    try {
      var hidden = card.querySelector('[data-vowella-font-value]');
      if (!hidden) return;
      var saved = store(storageKey);

      function applyFont(family, weight) {
        if (family) card.style.setProperty('--vowella-preview-font', family);
        card.style.setProperty('--vowella-preview-font-weight', weight || '400');
      }

      function select(btn, persist) {
        var all = grid.querySelectorAll('.vowella-font');
        for (var i = 0; i < all.length; i++) all[i].classList.toggle('is-selected', all[i] === btn);
        var val = btn.getAttribute('data-val');
        var family = btn.getAttribute('data-font-family');
        var weight = btn.getAttribute('data-font-weight');
        hidden.value = val;
        applyFont(family, weight);
        var form = getForm();
        if (form) mirrorField(form, hidden);
        if (persist) {
          var patch = {};
          patch.font = val;
          patch.font_family = family;
          patch.font_weight = weight;
          save(storageKey, patch);
        }
        updatePreviewState(card);
      }

      var initialBtn = null;
      if (saved.font) {
        initialBtn = grid.querySelector('.vowella-font[data-val="' + CSS.escape(saved.font) + '"]');
      }
      if (!initialBtn) initialBtn = grid.querySelector('.vowella-font.is-selected') || grid.querySelector('.vowella-font');
      if (initialBtn) select(initialBtn, false);

      grid.addEventListener('click', function (e) {
        var btn = e.target.closest('.vowella-font');
        if (!btn || !grid.contains(btn)) return;
        select(btn, true);
      });
    } catch (err) {
      if (window.console) console.error('Vowella customize: font grid init failed', err);
    }
  }

  /* ---- "Gift For" (Recipient) step (vowella-customize-id-bracelet.liquid) */

  function jumpGalleryToRecipient(val) {
    // The merchant wants clicking "For Her"/"For Him" to bring the
    // matching gallery photo to the front — same "prepend" behavior
    // Dawn's own media-gallery custom element exposes. Convention: tag
    // that photo's Alt Text with "Label_ForHim" / "Label_ForHer" in the
    // admin (case and punctuation don't matter, matched loosely below).
    // Purely additive — a product with no tagged photos yet just finds
    // no match and does nothing, never errors.
    try {
      var needle = val && /her/i.test(val) ? 'forher' : 'forhim';
      var gallery = document.querySelector('media-gallery');
      if (!gallery || typeof gallery.setActiveMedia !== 'function') return;
      var imgs = gallery.querySelectorAll('[data-media-id] img[alt]');
      for (var i = 0; i < imgs.length; i++) {
        var alt = (imgs[i].getAttribute('alt') || '').replace(/[^a-z]/gi, '').toLowerCase();
        if (alt.indexOf(needle) === -1) continue;
        var slide = imgs[i].closest('[data-media-id]');
        if (slide) gallery.setActiveMedia(slide.getAttribute('data-media-id'), true);
        return;
      }
    } catch (err) {
      if (window.console) console.error('Vowella customize: gallery jump failed', err);
    }
  }

  function initRecipientGrid(card, grid, storageKey) {
    try {
      var hidden = card.querySelector('[data-vowella-recipient-value]');
      var saved = store(storageKey);

      function select(btn, persist) {
        var all = grid.querySelectorAll('.vowella-recipient-option');
        for (var i = 0; i < all.length; i++) all[i].classList.toggle('is-selected', all[i] === btn);
        var val = btn.getAttribute('data-val');
        if (hidden) hidden.value = val;
        var form = getForm();
        if (form && hidden) mirrorField(form, hidden);
        if (persist) {
          var patch = {};
          patch.recipient = val;
          save(storageKey, patch);
        }
        jumpGalleryToRecipient(val);
      }

      if (saved.recipient) {
        var match = grid.querySelector('.vowella-recipient-option[data-val="' + CSS.escape(saved.recipient) + '"]');
        // Restored on page load only — deliberately doesn't re-jump the
        // gallery here (nothing to "jump" yet before the shopper acts).
        if (match) {
          match.classList.add('is-selected');
          if (hidden) hidden.value = saved.recipient;
        }
      }

      grid.addEventListener('click', function (e) {
        var btn = e.target.closest('.vowella-recipient-option');
        if (!btn || !grid.contains(btn)) return;
        select(btn, true);
      });
    } catch (err) {
      if (window.console) console.error('Vowella customize: recipient grid init failed', err);
    }
  }

  /* ---- "Her/His Bracelet Color" grids (couple's ID bracelet) -----------

     Sept 2026 (§35): NOT a real Shopify variant option (see item 8 in the
     file doc comment above for why that was reverted) — wired exactly
     like initGemGrid()'s birthstone grid above: click to select, one
     value written into a hidden line-item-property input, mirrored into
     the Add to Cart form. `key` ("a" for Her, "b" for His) matches the
     same a/b convention every other field on this product uses. */

  function initColorGrid(card, grid, storageKey) {
    try {
      var key = grid.getAttribute('data-vowella-color-grid');
      var hidden = card.querySelector('[data-vowella-color-value="' + key + '"]');
      var saved = store(storageKey);

      function select(opt, val, persist) {
        var all = grid.querySelectorAll('.vowella-color-option');
        for (var i = 0; i < all.length; i++) all[i].classList.toggle('is-selected', all[i] === opt);
        if (hidden) hidden.value = val;
        var form = getForm();
        if (form && hidden) mirrorField(form, hidden);
        if (persist) {
          var patch = {};
          patch[key] = val;
          save(storageKey, patch);
        }
        updatePreviewState(card);
      }

      var initialOpt = null;
      if (saved[key]) {
        initialOpt = grid.querySelector('.vowella-color-option[data-val="' + CSS.escape(saved[key]) + '"]');
      }
      // Always starts with a real selection (first swatch, if nothing was
      // saved) rather than leaving Color unset — there's no "unselected"
      // native-variant state to fall back on anymore, and the preview
      // needs a color to show the moment it opens.
      if (!initialOpt) initialOpt = grid.querySelector('.vowella-color-option');
      if (initialOpt) select(initialOpt, initialOpt.getAttribute('data-val'), false);

      grid.addEventListener('click', function (e) {
        var opt = e.target.closest('.vowella-color-option');
        if (!opt || !grid.contains(opt)) return;
        select(opt, opt.getAttribute('data-val'), true);
      });
    } catch (err) {
      if (window.console) console.error('Vowella customize: color grid init failed', err);
    }
  }

  /* ---- Preview panel ---------------------------------------------------- */

  function previewRefs(step) {
    if (!step) return null;
    var assetsScript = step.querySelector('[data-vowella-preview-assets]');
    if (!assetsScript) return null;
    var assets;
    try {
      assets = JSON.parse(assetsScript.textContent);
    } catch (e) {
      if (window.console) console.error('Vowella preview: could not parse asset map', e);
      return null;
    }
    return {
      step: step,
      assets: assets,
      mode: step.getAttribute('data-vowella-preview-mode') || 'gem',
      leftSource: assetsScript.getAttribute('data-vowella-preview-left-source') || 'b',
      rightSource: assetsScript.getAttribute('data-vowella-preview-right-source') || 'a',
      // Sept 2026 — heart-charm bracelet ("single-gem" mode): only one
      // birthstone step exists on that product, always rendered with
      // target 'a' (see vowella-customize-bracelet-heart.liquid), so 'a'
      // is the sane default here too — same override pattern as
      // left/right-source above, in case a future single-stone product
      // ever needs a different target letter.
      singleSource: assetsScript.getAttribute('data-vowella-preview-single-source') || 'a',
      // "bar-pair" mode (couple's ID bracelet SET): two independent photos
      // stacked in one frame — top/bottom instead of left/right, since
      // these are stacked vertically, not side by side. Defaults match the
      // same 'a'/'b' convention every other mode here uses (a = Her, b =
      // His, by the snippet's own convention).
      topSource: assetsScript.getAttribute('data-vowella-preview-top-source') || 'a',
      bottomSource: assetsScript.getAttribute('data-vowella-preview-bottom-source') || 'b',
      // Sept 2026 (§35): Her/His Bracelet Color are line-item properties
      // now (see initColorGrid() above), not a real variant, so these are
      // read the same way as topSource/bottomSource — via fieldValue(card,
      // source, 'color') in the "bar-pair" branch of renderPreview() below
      // — defaulting to the same 'a'/'b' keys the color grids themselves
      // use (data-vowella-color-grid="a"/"b" in the liquid).
      topColorSource: assetsScript.getAttribute('data-vowella-preview-top-color-source') || 'a',
      bottomColorSource: assetsScript.getAttribute('data-vowella-preview-bottom-color-source') || 'b',
      hint: step.querySelector('[data-vowella-preview-hint]'),
      button: step.querySelector('[data-vowella-preview-btn]'),
      frame: step.querySelector('[data-vowella-preview-frame]'),
      bg: step.querySelector('[data-vowella-preview-bg]'),
      bgTop: step.querySelector('[data-vowella-preview-bg="top"]'),
      bgBottom: step.querySelector('[data-vowella-preview-bg="bottom"]'),
      stoneLeft: step.querySelector('[data-vowella-preview-stone="left"]'),
      stoneRight: step.querySelector('[data-vowella-preview-stone="right"]'),
      stoneSingle: step.querySelector('[data-vowella-preview-stone="single"]'),
      textLeft: step.querySelector('[data-vowella-preview-text="left"]'),
      textRight: step.querySelector('[data-vowella-preview-text="right"]'),
      textSingle: step.querySelector('[data-vowella-preview-text="single"]'),
      textTop: step.querySelector('[data-vowella-preview-text="top"]'),
      textBottom: step.querySelector('[data-vowella-preview-text="bottom"]')
    };
  }

  function fieldValue(card, source, kind) {
    // kind: 'name', 'stone', or 'color'
    if (kind === 'name') {
      var input = card.querySelector('[data-vowella-cx-input="name_' + source + '"]');
      return input ? input.value.trim() : '';
    }
    if (kind === 'color') {
      var colorHidden = card.querySelector('[data-vowella-color-value="' + source + '"]');
      return colorHidden ? colorHidden.value : '';
    }
    var hidden = card.querySelector('[data-vowella-gem-value="' + source + '"]');
    return hidden ? hidden.value : '';
  }

  function monthFromGemValue(val) {
    // Gem values look like "January · Garnet" — the month is everything
    // before the separator.
    if (!val) return '';
    return val.split('·')[0].trim();
  }

  function selectedOptionValue(optionNameRegex) {
    // Dawn names swatch/button inputs "<Option>-<position>" (e.g. "Color-1")
    // and separately stamps data-option-name="Color" on the same input —
    // prefer that attribute since it's the option name with no suffix,
    // falling back to a name-substring match for the <select> case
    // (name="options[Color]") where data-option-name isn't present.
    var radios = document.querySelectorAll('variant-selects input[type="radio"], variant-selects input[type="checkbox"]');
    for (var i = 0; i < radios.length; i++) {
      var r = radios[i];
      var optName = r.getAttribute('data-option-name') || r.name || '';
      if (r.checked && optionNameRegex.test(optName)) return r.value;
    }
    var selects = document.querySelectorAll('variant-selects select');
    for (var j = 0; j < selects.length; j++) {
      if (optionNameRegex.test(selects[j].name)) return selects[j].value;
    }
    return '';
  }

  function selectedColorValue() {
    return selectedOptionValue(/colou?r/i);
  }

  function backgroundUrlFor(assets, colorValue) {
    if (colorValue && /silver/i.test(colorValue) && assets.bg.silver) return assets.bg.silver;
    return assets.bg.gold || assets.bg.silver || '';
  }

  function colorBackgroundUrlFor3(assets, colorValue) {
    // Same idea as backgroundUrlFor() above, but for a 3-way Black/Gold/
    // Silver palette (the couple's ID bracelet) instead of Gold/Silver —
    // kept as its own function rather than adding a 3rd branch to
    // backgroundUrlFor() so nothing already using that one can change
    // behavior by accident. Expects assets.bg shaped like:
    //   { "black": url, "gold": url, "silver": url }
    if (colorValue && /black/i.test(colorValue) && assets.bg.black) return assets.bg.black;
    if (colorValue && /silver/i.test(colorValue) && assets.bg.silver) return assets.bg.silver;
    return assets.bg.gold || assets.bg.silver || assets.bg.black || '';
  }

  function previewIsComplete(card, refs) {
    if (refs.mode === 'name') {
      // Bracelet preview: just the two names, no birthstone step involved.
      return Boolean(
        fieldValue(card, refs.leftSource, 'name') && fieldValue(card, refs.rightSource, 'name')
      );
    }
    if (refs.mode === 'single-gem') {
      // Heart-charm bracelet: one birthstone, no name field at all.
      return Boolean(fieldValue(card, refs.singleSource, 'stone'));
    }
    if (refs.mode === 'single-name') {
      // Bar ID bracelet: one engraving-text field, no birthstone step.
      return Boolean(fieldValue(card, refs.singleSource, 'name'));
    }
    if (refs.mode === 'bar-pair') {
      // Couple's ID bracelet SET: one shared Preview button for both
      // photos, so it only enables once BOTH engravings are filled in.
      return Boolean(
        fieldValue(card, refs.topSource, 'name') && fieldValue(card, refs.bottomSource, 'name')
      );
    }
    return Boolean(
      fieldValue(card, refs.leftSource, 'name') &&
        fieldValue(card, refs.leftSource, 'stone') &&
        fieldValue(card, refs.rightSource, 'name') &&
        fieldValue(card, refs.rightSource, 'stone')
    );
  }

  /**
   * Sept 2026 (§25): shrink-to-fit for the preview name text.
   *
   * Reads the fixed box width from CSS (--vowella-preview-text-max-width,
   * set per side under .vowella-preview--bracelet in vowella-custom.css —
   * unset/"none" on the necklace, which opts out entirely) and, only if
   * the name doesn't fit at the CSS default font-size, scales the
   * font-size down just enough to fit — never wraps, never spills past
   * the box. Short names are untouched (this is a no-op below the box's
   * width). Floored at 50% of the default size so a 13-character name
   * (the input's own maxlength) never becomes unreadably tiny; text stays
   * fully visible past that floor rather than getting clipped — same
   * "a little off is fine, never fully broken" preference as elsewhere
   * in this customization flow.
   *
   * Safe to call unconditionally on any .vowella-preview__text element:
   * with no CSS width set (the necklace today), the box auto-sizes to
   * its own content, so clientWidth === scrollWidth and this returns
   * immediately without touching font-size.
   */
  function fitPreviewText(el, minScale) {
    if (!el) return;
    el.style.fontSize = ''; // always re-measure against the CSS default first
    if (!el.textContent) return;
    var maxWidth = el.clientWidth;
    if (!maxWidth || el.scrollWidth <= maxWidth) return;
    var baseSize = parseFloat(window.getComputedStyle(el).fontSize);
    if (!baseSize) return;
    // minScale is a floor on how far this can shrink, as a fraction of the
    // CSS default size — omit it (as every caller except the id-bracelet's
    // "bar-pair" mode does) to keep the original 50% floor, which exists so
    // a maxed-out name never becomes illegibly tiny. The id-bracelet tag
    // passes 0: the client explicitly wants engravings to ALWAYS just keep
    // shrinking to fit, never truncate/ellipsize (see the "…" it produced
    // once this floor was hit on a real 20-char engraving) — with no floor,
    // the loop below can shrink all the way down until it actually fits.
    var floor = typeof minScale === 'number' ? minScale : 0.5;
    var minSize = baseSize * floor;
    // A single proportional guess (newSize = size * maxWidth/natural) is
    // only an ESTIMATE — font metrics don't scale perfectly linearly with
    // font-size (sub-pixel rounding, hinting, kerning) — so one pass can
    // still leave a few px of overflow, which is exactly what was tripping
    // the .vowella-preview__text--single CSS ellipsis fallback on real
    // engravings that should never have needed it (a first attempt at
    // fixing this used a fixed small percentage per retry, which wasn't
    // always enough correction within its iteration budget). Re-measuring
    // and re-applying the SAME proportional correction fresh each pass
    // converges fast (typically 1-3 passes) regardless of how far off the
    // first guess was, because each pass targets the exact ratio needed at
    // its own measurement — a fixed-point iteration, not a fixed nibble.
    var size = baseSize;
    var guard = 12;
    var originalTextOverflow = el.style.textOverflow;
el.style.textOverflow = 'clip';
    while (guard-- > 0) {
      var natural = el.scrollWidth;
      if (natural <= maxWidth) break;
      if (size <= minSize) {
        size = minSize;
        el.style.fontSize = size + 'px';
        break;
      }
      var next = size * (maxWidth / natural);
      if (next < minSize) next = minSize;
      if (next >= size) next = size * 0.8; // safety net: force progress either way
      size = next;
      el.style.fontSize = size + 'px';
    }
  }

  function renderPreview(card, refs) {
    if (refs.mode === 'name') {
      // Bracelet preview: one fixed base photo (no per-variant swap — the
      // client confirmed the single white-background product photo is the
      // base to use), just the two names composited on top.
      // Sept 2026 (§22): reverted from curved SVG <textPath> text back to
      // plain positioned/rotated divs — same textContent assignment the
      // "gem" (necklace) branch below uses. See the comment above
      // .vowella-preview--bracelet in assets/vowella-custom.css for why.
      if (refs.bg && refs.assets.bg) refs.bg.src = refs.assets.bg.default || refs.assets.bg.gold || refs.assets.bg.silver || '';
      if (refs.textLeft) refs.textLeft.textContent = fieldValue(card, refs.leftSource, 'name');
      if (refs.textRight) refs.textRight.textContent = fieldValue(card, refs.rightSource, 'name');
      fitPreviewText(refs.textLeft);
      fitPreviewText(refs.textRight);
      return;
    }

    if (refs.mode === 'single-gem') {
      // Heart-charm bracelet: background swaps with the Color variant
      // (same backgroundUrlFor() the necklace's "gem" mode uses below),
      // exactly one stone overlay, no text nodes to touch at all.
      var singleMonth = monthFromGemValue(fieldValue(card, refs.singleSource, 'stone'));
      var singleStoneUrl = refs.assets.stones[singleMonth];
      if (refs.bg) refs.bg.src = backgroundUrlFor(refs.assets, selectedColorValue());
      if (refs.stoneSingle && singleStoneUrl) refs.stoneSingle.src = singleStoneUrl;
      return;
    }

    if (refs.mode === 'single-name') {
      // ID bracelet(s): background swaps with the Color variant (Black/
      // Gold/Silver — see colorBackgroundUrlFor3()). On the couple's
      // bracelet this step is already scoped to ONE physical bracelet
      // (Her or His — each gets its own <script> asset map and its own
      // fixed tag position via a modifier class in the liquid, see
      // .vowella-preview--id-bracelet-her/-him in vowella-custom.css), so
      // there's no Style variant to read here at all.
      if (refs.bg) refs.bg.src = colorBackgroundUrlFor3(refs.assets, selectedColorValue());
      if (refs.textSingle) refs.textSingle.textContent = fieldValue(card, refs.singleSource, 'name');
      fitPreviewText(refs.textSingle);
      return;
    }

    if (refs.mode === 'bar-pair') {
      // Couple's ID bracelet SET: two independent photos in ONE frame —
      // Her on top, His on bottom. Sept 2026 (§35): Her and His Bracelet
      // Color are both line-item properties (initColorGrid() above), so
      // each is read via fieldValue() exactly like the engraving text
      // fields are, NOT a real variant — colorBackgroundUrlFor3() itself
      // is unchanged either way; the asset map is just nested one level
      // deeper here, {"top": {"bg": {...}}, "bottom": {"bg": {...}}}, so
      // each call gets just its own half.
      var topColor = fieldValue(card, refs.topColorSource, 'color');
      var bottomColor = fieldValue(card, refs.bottomColorSource, 'color');
      if (refs.bgTop && refs.assets.top) refs.bgTop.src = colorBackgroundUrlFor3(refs.assets.top, topColor);
      if (refs.bgBottom && refs.assets.bottom) refs.bgBottom.src = colorBackgroundUrlFor3(refs.assets.bottom, bottomColor);
      if (refs.textTop) refs.textTop.textContent = fieldValue(card, refs.topSource, 'name');
      if (refs.textBottom) refs.textBottom.textContent = fieldValue(card, refs.bottomSource, 'name');
      // minScale 0: no shrink floor for this tag — see the comment on
      // fitPreviewText() itself for why (client wants shrink-only, never
      // an ellipsis/truncated engraving).
      fitPreviewText(refs.textTop, 0);
      fitPreviewText(refs.textBottom, 0);
      return;
    }

    var leftMonth = monthFromGemValue(fieldValue(card, refs.leftSource, 'stone'));
    var rightMonth = monthFromGemValue(fieldValue(card, refs.rightSource, 'stone'));
    var leftStoneUrls = refs.assets.stones[leftMonth];
    var rightStoneUrls = refs.assets.stones[rightMonth];

    if (refs.bg) refs.bg.src = backgroundUrlFor(refs.assets, selectedColorValue());
    if (refs.stoneLeft && leftStoneUrls) refs.stoneLeft.src = leftStoneUrls.left;
    if (refs.stoneRight && rightStoneUrls) refs.stoneRight.src = rightStoneUrls.right;
    if (refs.textLeft) refs.textLeft.textContent = fieldValue(card, refs.leftSource, 'name');
    if (refs.textRight) refs.textRight.textContent = fieldValue(card, refs.rightSource, 'name');
    fitPreviewText(refs.textLeft);
    fitPreviewText(refs.textRight);
  }

  function updateOnePreviewState(card, step) {
    var refs = previewRefs(step);
    if (!refs) return;

    var ready = previewIsComplete(card, refs);

    if (refs.button) refs.button.disabled = !ready;
    if (refs.hint) {
      var notReadyMessage =
        refs.mode === 'single-gem'
          ? 'Choose a birthstone above, then preview your piece.'
          : refs.mode === 'single-name'
          ? 'Enter your engraving above, then preview your piece.'
          : refs.mode === 'bar-pair'
          ? 'Enter both engravings above, then preview your set.'
          : 'Fill in both names and birthstones above, then preview your piece.';
      refs.hint.textContent = ready ? 'Ready — see your piece before you add it to cart.' : notReadyMessage;
      refs.hint.classList.toggle('is-ready', ready);
    }

    // Once the preview has been opened, keep it live-synced with further edits.
    if (refs.frame && !refs.frame.hidden) {
      if (ready) {
        renderPreview(card, refs);
      } else {
        refs.frame.hidden = true;
      }
    }
  }

  function updatePreviewState(card) {
    // A card can hold more than one preview step (couple's ID bracelet:
    // one for Her, one for His) — refresh every one of them, since a
    // single field edit (e.g. Color variant) can affect more than one.
    var steps = card.querySelectorAll('[data-vowella-preview-step]');
    for (var i = 0; i < steps.length; i++) updateOnePreviewState(card, steps[i]);
  }

  function initOnePreview(card, step) {
    var refs = previewRefs(step);
    if (!refs || !refs.button) return;

    updateOnePreviewState(card, step);

    refs.button.addEventListener('click', function () {
      if (refs.button.disabled) return;
      // Unhide first, then render.
      refs.frame.hidden = false;
      renderPreview(card, refs);
      refs.frame.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });

    // Re-sync the background photo (and nothing else) when the shopper
    // changes the Color variant, but only once this step's preview is
    // already open.
    document.addEventListener('change', function (e) {
      var target = e.target;
      if (!target || !target.name || !/colou?r/i.test(target.name)) return;
      if (refs.frame && !refs.frame.hidden) renderPreview(card, refs);
    });
  }

  function initPreview(card) {
    var steps = card.querySelectorAll('[data-vowella-preview-step]');
    for (var i = 0; i < steps.length; i++) initOnePreview(card, steps[i]);
  }

  function init(card) {
    if (card.dataset.vowellaCxInit === '1') return;
    card.dataset.vowellaCxInit = '1';

    var pid = card.getAttribute('data-product-id') || 'p';
    var storageKey = 'vowella_cx_' + pid;

    wire(card);

    var fields = card.querySelectorAll(
      '[data-vowella-cx-input]:not([data-vowella-gem-value]):not([data-vowella-font-value]):not([data-vowella-recipient-value]):not([data-vowella-color-value])'
    );
    for (var i = 0; i < fields.length; i++) initTextField(card, fields[i], storageKey);

    var grids = card.querySelectorAll('[data-vowella-gem-grid]');
    for (var j = 0; j < grids.length; j++) initGemGrid(card, grids[j], storageKey);

    var fontGrids = card.querySelectorAll('[data-vowella-font-grid]');
    for (var k = 0; k < fontGrids.length; k++) initFontGrid(card, fontGrids[k], storageKey);

    var recipientGrids = card.querySelectorAll('[data-vowella-recipient-grid]');
    for (var m = 0; m < recipientGrids.length; m++) initRecipientGrid(card, recipientGrids[m], storageKey);

    var colorGrids = card.querySelectorAll('[data-vowella-color-grid]');
    for (var n = 0; n < colorGrids.length; n++) initColorGrid(card, colorGrids[n], storageKey);

    initPreview(card);
  }

  function boot() {
    var cards = document.querySelectorAll('[data-vowella-cx]');
    for (var i = 0; i < cards.length; i++) {
      init(cards[i]);
      wire(cards[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();